// Frontend: TicketList.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FaList, FaPlus, FaCheckCircle, FaClock, FaTimesCircle, 
  FaEye, FaEdit, FaUserShield, FaHourglassHalf, FaCheck, FaTimes, 
  FaFilter, FaSearch, FaDownload, FaSync, FaInfoCircle } from 'react-icons/fa';
import API_BASE_URL from '../../components/apiconfig/api-config';
import axios from 'axios';
import NoRecordFound from '../../components/common/no-record-found';

const ROWS_PER_PAGE = 25;

const TicketList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [reviewComment, setReviewComment] = useState('');
  const [completionStatus, setCompletionStatus] = useState('');
  const [developerNotes, setDeveloperNotes] = useState('');
  const [userRole, setUserRole] = useState('');
  const [userId, setUserId] = useState(null);
  const [ticketLogs, setTicketLogs] = useState([]);
  const [returnCount, setReturnCount] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isReviewSubmitting, setIsReviewSubmitting] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    completionStatus: '',
    priority: '',
    category: ''
  });

  const fetchUserRole = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/profile/profile-get`, {
        withCredentials: true
      });
      if (response.data.success) {
        const role = response.data.data.role;
        setUserRole(role);
       if (response.data.data.id) {              
        setUserId(Number(response.data.data.id));
      }
      }
    } catch (error) {
      console.error('Error fetching user role:', error);
    }
  }, []);
const handleRefresh = async () => {
  setIsRefreshing(true);
  try {
    await fetchTickets();
  } catch (error) {
    console.error('Error refreshing tickets:', error);
  } finally {
    setIsRefreshing(false);
  }
};

  const fetchTickets = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/ticket-raise-get`, {
        withCredentials: true
      });
      
      if (response.data && Array.isArray(response.data)) {
        setTickets(response.data);
        setTotal(response.data.length);
      } else {
        setTickets([]);
        setTotal(0);
      }
    } catch (error) {
      console.error('Error fetching tickets:', error);
      setTickets([]);
      setTotal(0);
    } finally {
      setFetching(false);
    }
  }, []);

  // Filter tickets
  useEffect(() => {
    let filtered = [...tickets];
    
    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(ticket => 
        (ticket.title && ticket.title.toLowerCase().includes(searchLower)) ||
        (ticket.ticket_number && ticket.ticket_number.toLowerCase().includes(searchLower)) ||
        (ticket.description && ticket.description.toLowerCase().includes(searchLower)) ||
        (ticket.name && ticket.name.toLowerCase().includes(searchLower))
      );
    }
    
    // Status filter
    if (filters.status) {
      filtered = filtered.filter(ticket => ticket.status === filters.status);
    }
    
    // Completion status filter
    if (filters.completionStatus) {
      filtered = filtered.filter(ticket => ticket.completion_status === filters.completionStatus);
    }
    
    // Priority filter
    if (filters.priority) {
      filtered = filtered.filter(ticket => ticket.priority === filters.priority);
    }
    
    // Category filter
    if (filters.category) {
      filtered = filtered.filter(ticket => ticket.category === filters.category);
    }
    
    setFilteredTickets(filtered);
    setTotal(filtered.length);
    setPage(1); // Reset to first page when filters change
  }, [tickets, filters]);

  // Pagination handlers
  const totalPages = Math.ceil(total / ROWS_PER_PAGE);
  const startIndex = (page - 1) * ROWS_PER_PAGE;
  const endIndex = startIndex + ROWS_PER_PAGE;
  const paginatedTickets = filteredTickets.slice(startIndex, endIndex);

  // Count active filters
  const activeFilterCount = Object.values(filters).filter(f => f !== '').length;

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      search: '',
      status: '',
      completionStatus: '',
      priority: '',
      category: ''
    });
  };

  const handlePrevPage = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (page < totalPages) {
      setPage(page + 1);
    }
  };

  // Fetch tickets and user info on component mount
  useEffect(() => {
    fetchTickets();
    fetchUserRole();
  }, [fetchTickets, fetchUserRole]);

  // Handle URL query parameters for email links (approve/reject)
  useEffect(() => {
    const action = searchParams.get('action');
    const ticketId = searchParams.get('id');
    
    if (action && ticketId && tickets.length > 0) {
      const ticket = tickets.find(t => t.id === parseInt(ticketId));
      if (ticket) {
        if (action === 'approve') {
          setSelectedTicket(ticket);
          setAdminNotes(ticket.admin_notes || '');
          setShowApproveModal(true);
          setSearchParams({});
        } else if (action === 'reject') {
          setSelectedTicket(ticket);
          setAdminNotes(ticket.admin_notes || '');
          setShowApproveModal(true);
          setSearchParams({});
        }
      }
    }
  }, [searchParams, tickets, setSearchParams]);

  const handleApprove = async (status) => {
    // Check if user is admin
    if (userRole !== 'Admin' && userRole !== 'admin') {
      alert('Only admin can approve or reject tickets');
      return;
    }

    setIsSubmitting(true);
    try {
      // Auto-set completion status based on approval status
      let autoCompletionStatus;
      if (status === 'approved') {
        autoCompletionStatus = 'pending_approval';
      } else if (status === 'rejected') {
        autoCompletionStatus = 'rejected';
      } else if (status === 'refer_back') {
        autoCompletionStatus = 'refer_back';
      } else {
        autoCompletionStatus = 'pending_approval';
      }

      const response = await axios.put(
        `${API_BASE_URL}/api/ticket-raise-update/${selectedTicket.id}`,
        {
          status: status,
          admin_notes: adminNotes,
          completion_status: autoCompletionStatus
        },
        {
          withCredentials: true
        }
      );
      
      if (response.data) {
        const updatedTicket = response.data;
        setTickets(tickets.map(ticket => 
          ticket.id === updatedTicket.id ? updatedTicket : ticket
        ));
        setShowApproveModal(false);
        setAdminNotes('');
        setCompletionStatus('');
        setPage(1);
        alert(`Ticket ${status} successfully!`);
        fetchTickets();
      }
    } catch (error) {
      console.error('Error updating ticket:', error);
      const errorMessage = error.response?.data?.error || 'Error updating ticket. Please try again.';
      alert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const viewTicketDetails = async (ticket) => {
    setSelectedTicket(ticket);
    setShowDetailsModal(true);
    
    // Fetch ticket logs
    try {
      const response = await axios.get(`${API_BASE_URL}/api/tickets/${ticket.id}`, {
        withCredentials: true
      });
      
      if (response.data) {
        setTicketLogs(response.data.logs || []);
        setReturnCount(response.data.returnCount || 0);
      }
    } catch (error) {
      console.error('Error fetching ticket logs:', error);
      setTicketLogs([]);
      setReturnCount(0);
    }
  };

  const openApproveModal = (ticket) => {
    setSelectedTicket(ticket);
    setAdminNotes(ticket.admin_notes || '');
    setCompletionStatus(ticket.completion_status || '');
    setShowApproveModal(true);
  };

  const openCompletionModal = (ticket) => {
    setSelectedTicket(ticket);
    setCompletionStatus(ticket.completion_status || '');
    setDeveloperNotes(ticket.developer_notes || '');
    setShowCompletionModal(true);
  };

  const openReviewModal = (ticket) => {
    setSelectedTicket(ticket);
    setReviewComment('');
    setShowReviewModal(true);
  };

  const handleReviewTicket = async (action) => {
    if (action === 'return' && !reviewComment.trim()) {
      alert('Please provide a review comment when returning ticket to developer');
      return;
    }

    setIsReviewSubmitting(true);
    try {
      const response = await axios.put(
        `${API_BASE_URL}/api/ticket-review/${selectedTicket.id}`,
        {
          action: action,
          review_comment: reviewComment
        },
        {
          withCredentials: true
        }
      );
      
      if (response.data) {
        setShowReviewModal(false);
        setReviewComment('');
        alert(action === 'close' ? 'Ticket closed successfully!' : 'Ticket returned to developer for rework!');
        fetchTickets();
      }
    } catch (error) {
      console.error('Error reviewing ticket:', error);
      const errorMessage = error.response?.data?.error || 'Error reviewing ticket. Please try again.';
      alert(errorMessage);
    } finally {
      setIsReviewSubmitting(false);
    }
  };

  const handleUpdateCompletion = async () => {
    if (!completionStatus) {
      alert('Please select a completion status');
      return;
    }

    try {
      const response = await axios.put(
        `${API_BASE_URL}/api/ticket-raise-completion/${selectedTicket.id}`,
        {
          completion_status: completionStatus,
          developer_notes: developerNotes
        },
        {
          withCredentials: true
        }
      );
      
      if (response.data) {
        const updatedTicket = response.data;
        setTickets(tickets.map(ticket => 
          ticket.id === updatedTicket.id ? updatedTicket : ticket
        ));
        setShowCompletionModal(false);
        setCompletionStatus('');
        setDeveloperNotes('');
        alert('Completion status updated successfully!');
        fetchTickets();
      }
    } catch (error) {
      console.error('Error updating completion status:', error);
      const errorMessage = error.response?.data?.error || 'Error updating completion status. Please try again.';
      alert(errorMessage);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <FaCheckCircle className="text-green-500" />;
      case 'rejected':
        return <FaTimesCircle className="text-red-500" />;
      case 'refer_back':
        return <FaClock className="text-yellow-500" />;
      default:
        return <FaClock className="text-yellow-500" />;
    }
  };

  const getCompletionStatusBadge = (ticket) => {
    const completionStatus = ticket.completion_status || 'pending_approval';
    
    const statusConfig = {
      'pending_approval': {
        status: 'Pending Approval',
        icon: <FaClock className="text-yellow-500" />,
        class: 'bg-yellow-100 text-yellow-800'
      },
      'approved': {
        status: 'Approved',
        icon: <FaCheckCircle className="text-green-500" />,
        class: 'bg-green-100 text-green-800'
      },
      'rejected': {
        status: 'Rejected',
        icon: <FaTimesCircle className="text-red-500" />,
        class: 'bg-red-100 text-red-800'
      },
      'in_progress': {
        status: 'In Progress',
        icon: <FaHourglassHalf className="text-blue-500" />,
        class: 'bg-blue-100 text-blue-800'
      },
      'completed': {
        status: 'Completed',
        icon: <FaCheckCircle className="text-green-500" />,
        class: 'bg-green-100 text-green-800'
      },
      'closed': {
        status: 'Closed',
        icon: <FaCheckCircle className="text-gray-500" />,
        class: 'bg-gray-100 text-gray-800'
      },
      'not_applicable': {
        status: 'Not Applicable',
        icon: <FaTimesCircle className="text-gray-500" />,
        class: 'bg-gray-100 text-gray-800'
      },
      'refer_back': {
        status: 'Refer Back',
        icon: <FaClock className="text-yellow-500" />,
        class: 'bg-yellow-100 text-yellow-800'
      }
    };

    const config = statusConfig[completionStatus] || statusConfig['pending_approval'];
    
    return (
      <div className="flex items-center">
        {config.icon}
        <span className={`ml-2 px-2 py-1 rounded-full text-xs font-semibold ${config.class}`}>
          {config.status}
        </span>
      </div>
    );
  };

  const getPriorityBadge = (priority) => {
    const classes = {
      high: 'bg-red-100 text-red-800',
      medium: 'bg-yellow-100 text-yellow-800',
      low: 'bg-blue-100 text-blue-800'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${classes[priority]}`}>
        {priority}
      </span>
    );
  };

  const getCategoryBadge = (category) => {
    const classes = {
      'change-record-in-database': 'bg-indigo-100 text-indigo-800',
      'new-feature': 'bg-green-100 text-green-800',
      'new-functionality': 'bg-teal-100 text-teal-800',
      'formula-update': 'bg-purple-100 text-purple-800',
      'ui-ux': 'bg-blue-100 text-blue-800',
      'new-module': 'bg-pink-100 text-pink-800',
      'new-report': 'bg-orange-100 text-orange-800'
    };
  
    const displayNames = {
      'change-record-in-database': 'Change Record in Database',
      'new-feature': 'New Feature',
      'new-functionality': 'New Functionality / Update',
      'formula-update': 'Formula Update',
      'ui-ux': 'User Interface',
      'new-module': 'New Module',
      'new-report': 'New Report'
    };
  
    const badgeClass =
      classes[category] || 'bg-gray-100 text-gray-800';
  
    const badgeLabel =
      displayNames[category] || category;
  
    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-semibold ${badgeClass}`}
      >
        {badgeLabel}
      </span>
    );
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = String(d.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
  };

  if (fetching) {
    return (
      <div className="space-y-4 sm:space-y-6 p-3 sm:p-0">
        <div className="bg-white p-3 sm:p-4 rounded overflow-hidden">
          <div className="flex flex-col items-center justify-center h-48">
            <div className="animate-spin h-8 w-8 sm:h-10 sm:w-10 border-4 border-[#10a5e9] border-t-transparent rounded-full mb-2" />
            <span className="text-gray-600 text-xs sm:text-sm">Loading tickets...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 p-3 sm:p-0">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <h1 className="text-xl sm:text-2xl font-semibold text-gray-800">
          {(userRole === 'Admin' || userRole === 'admin' || userRole === 'developer' || userRole === 'Developer') ? 'Ticket Management' : 'Ticket List'}
        </h1>
        <div className="flex items-center gap-3 flex-wrap">
          {/* Ticket Count Badges */}
          <div className="flex items-center gap-2">
            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
              Total: {tickets.length}
            </span>
            {activeFilterCount > 0 && (
              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                Filtered: {filteredTickets.length}
              </span>
            )}
          </div>
            <button
    onClick={handleRefresh}
    disabled={isRefreshing}
    className={`flex items-center gap-2 px-3 py-2 font-medium rounded-md text-sm transition-colors ${
      isRefreshing 
        ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
        : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
    }`}
    title="Refresh Records"
  >
    <FaSync className={`text-sm ${isRefreshing ? 'animate-spin' : ''}`} />
    {isRefreshing ? 'Refreshing...' : 'Refresh'}
  </button>
          {(userRole === 'Admin' || userRole === 'admin') && (
            <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
              <FaUserShield className="mr-1" /> Admin Mode
            </div>
          )}
          {(userRole === 'developer' || userRole === 'Developer') && (
            <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
              <FaUserShield className="mr-1" /> Developer Mode
            </div>
          )}
          <button
            onClick={() => navigate('/ticket-raise')}
            className="flex items-center gap-2 px-3 py-2 bg-[#10a5e9] hover:bg-blue-700 text-white font-medium rounded-md text-sm"
          >
            <FaPlus /> Raise Ticket
          </button>
        </div>
      </div>

      <div className="bg-white p-3 sm:p-4 rounded overflow-hidden">
        <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-700">
            {(userRole === 'Admin' || userRole === 'admin' || userRole === 'developer' || userRole === 'Developer') ? 'All Requests' : 'Your Requests'}
          </h2>
          <div className="flex items-center gap-2">
            {activeFilterCount > 0 && (
              <span className="text-xs text-gray-600">
                Showing {filteredTickets.length} of {tickets.length} tickets
              </span>
            )}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-md text-sm"
            >
              <FaFilter />
              Filters
              {activeFilterCount > 0 && (
                <span className="px-2 py-0.5 bg-[#10a5e9] text-white rounded-full text-xs">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Search Filter */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Search</label>
                <div className="relative">
                  <FaSearch className="absolute left-2 top-2.5 text-gray-400 text-xs" />
                  <input
                    type="text"
                    value={filters.search}
                    onChange={(e) => setFilters({...filters, search: e.target.value})}
                    placeholder="Search tickets..."
                    className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-[#10a5e9] focus:border-[#10a5e9]"
                  />
                </div>
              </div>

              {/* Status Filter */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Admin Approval</label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters({...filters, status: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-[#10a5e9] focus:border-[#10a5e9]"
                >
                  <option value="">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                  <option value="refer_back">Refer Back</option>
                </select>
              </div>

              {/* Completion Status Filter */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Completion Status</label>
                <select
                  value={filters.completionStatus}
                  onChange={(e) => setFilters({...filters, completionStatus: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-[#10a5e9] focus:border-[#10a5e9]"
                >
                  <option value="">All Completion Statuses</option>
                  <option value="pending_approval">Pending Approval</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="closed">Closed</option>
                  <option value="rejected">Rejected</option>
                  <option value="refer_back">Refer Back</option>
                  <option value="not_applicable">Not Applicable</option>
                </select>
              </div>

              {/* Priority Filter */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Priority</label>
                <select
                  value={filters.priority}
                  onChange={(e) => setFilters({...filters, priority: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-[#10a5e9] focus:border-[#10a5e9]"
                >
                  <option value="">All Priorities</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>

              {/* Category Filter */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
                <select
                  value={filters.category}
                  onChange={(e) => setFilters({...filters, category: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-[#10a5e9] focus:border-[#10a5e9]"
                >
                  <option value="">All Categories</option>
                  <option value="change-record-in-database">Change Record in Database</option>
                  <option value="new-feature">New Feature</option>
                  <option value="new-functionality/update">New Functionality / Update</option>
                  <option value="formula-update">Formula Update</option>
                  <option value="ui-ux">User Interface</option>
                  <option value="new-module">New Module</option>
                  <option value="new-report">New Report</option>
                </select>
              </div>

              {/* Clear Filters Button */}
              <div className="flex items-end">
                <button
                  onClick={clearFilters}
                  className="w-full px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium rounded-md text-sm"
                >
                  Clear All Filters
                </button>
              </div>
            </div>
          </div>
        )}

        {tickets.length > 0 ? (
          <>
            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-gray-100 text-left">
                    <th className="px-3 py-2 border border-gray-300 font-semibold text-gray-700">#</th>
                    <th className="px-3 py-2 border border-gray-300 font-semibold text-gray-700">Ticket ID</th>
                    <th className="px-3 py-2 border border-gray-300 font-semibold text-gray-700">Title</th>
                    <th className="px-3 py-2 border border-gray-300 font-semibold text-gray-700">Priority</th>
                    <th className="px-3 py-2 border border-gray-300 font-semibold text-gray-700">Category</th>
                    <th className="px-3 py-2 border border-gray-300 font-semibold text-gray-700">Admin Approval</th>
                    <th className="px-3 py-2 border border-gray-300 font-semibold text-gray-700">Completion</th>
                    <th className="px-3 py-2 border border-gray-300 font-semibold text-gray-700">Applied By</th>
                    <th className="px-3 py-2 border border-gray-300 font-semibold text-gray-700">Date</th>
                    <th className="px-3 py-2 border border-gray-300 font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedTickets.map((ticket, index) => (
                    <tr key={ticket.id} className="hover:bg-blue-50">
                      <td className="px-3 py-1 border border-gray-200">
                        {startIndex + index + 1}
                      </td>
                      <td className="px-3 py-1 border border-gray-200 font-medium text-gray-900">
                        {ticket.ticket_number}
                      </td>
                      <td className="px-3 py-1 border border-gray-200 max-w-xs truncate">
                        {ticket.title}
                      </td>
                      <td className="px-3 py-1 border border-gray-200">
                        {getPriorityBadge(ticket.priority)}
                      </td>
                      <td className="px-3 py-1 border border-gray-200">
                        {getCategoryBadge(ticket.category)}
                      </td>
                      <td className="px-3 py-1 border border-gray-200">
                        <div className="flex items-center">
                          {getStatusIcon(ticket.status)}
                          <span className="ml-2 capitalize">{ticket.status}</span>
                        </div>
                      </td>
                      <td className="px-3 py-1 border border-gray-200">
                        <div className="flex items-center justify-between w-full">
                          {getCompletionStatusBadge(ticket)}
                          {ticket.completion_status === 'completed' && (
                            <div className="relative group ml-auto">
                              <FaInfoCircle 
                                className="text-blue-500 cursor-help hover:text-blue-700 transition-colors" 
                                title="Kindly review the completed ticket. If all requirements are met, please proceed to close the ticket. Thank you."
                              />
                              <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                                Kindly review the completed ticket. If all requirements are met, please proceed to close the ticket. Thank you.
                                <div className="absolute top-full right-4 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-1 border border-gray-200">
                        {ticket.name || 'N/A'}
                      </td>
                      <td className="px-3 py-1 border border-gray-200">
                        {formatDate(ticket.created_at)}
                      </td>
                      <td className="px-3 py-1 border border-gray-200">
                        <div className="flex items-center gap-1 flex-wrap">
                          <button
                            onClick={() => viewTicketDetails(ticket)}
                            className="flex items-center gap-1 px-2 py-1 text-[#10a5e9] hover:bg-[#10a5e9] hover:text-white rounded text-xs font-medium transition-colors"
                            title="View Details"
                          >
                            <FaEye className="text-xs" />
                            <span>View</span>
                          </button>
                          {/* Edit Button for Refer Back Tickets */}
                          {(ticket.status === 'refer_back' || ticket.completion_status === 'refer_back') && 
                            Number(ticket.user_id) === Number(userId) && (
                            <button
                              onClick={() => navigate(`/ticket-raise?id=${ticket.id}`)}
                              className="flex items-center gap-1 px-2 py-1 text-orange-600 hover:bg-orange-600 hover:text-white rounded text-xs font-medium transition-colors"
                              title="Edit Ticket"
                            >
                              <FaEdit className="text-xs" />
                              <span>Edit</span>
                            </button>
                          )}
                          {(userRole === 'Admin' || userRole === 'admin') && ticket.status === 'pending' && (
                            <button
                              onClick={() => openApproveModal(ticket)}
                              className="flex items-center gap-1 px-2 py-1 text-green-600 hover:bg-green-600 hover:text-white rounded text-xs font-medium transition-colors"
                              title="Approve/Reject"
                            >
                              <FaEdit className="text-xs" />
                              <span>Review</span>
                            </button>
                          )}
                          {(userRole === 'developer' || userRole === 'Developer') && ticket.status === 'approved' && (
                            <button
                              onClick={() => openCompletionModal(ticket)}
                              className="flex items-center gap-1 px-2 py-1 text-blue-600 hover:bg-blue-600 hover:text-white rounded text-xs font-medium transition-colors"
                              title="Update Completion Status"
                            >
                              <FaEdit className="text-xs" />
                              <span>Update</span>
                            </button>
                          )}
                          {ticket.completion_status === 'completed' && (
                            <button
                              onClick={() => openReviewModal(ticket)}
                              className="flex items-center gap-1 px-2 py-1 text-green-600 hover:bg-green-600 hover:text-white rounded text-xs font-medium transition-colors"
                              title="Review Ticket"
                            >
                              <FaCheckCircle className="text-xs" />
                              <span>Review</span>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden space-y-3">
              {paginatedTickets.map((ticket, index) => (
                <div key={ticket.id} className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-semibold text-gray-500">#{startIndex + index + 1}</span>
                        <span className="text-sm font-bold text-gray-900">{ticket.ticket_number}</span>
                      </div>
                      <h3 className="text-sm font-semibold text-gray-900 mb-2">{ticket.title}</h3>
                    </div>
                    <div className="flex items-center gap-1 flex-wrap">
                      <button
                        onClick={() => viewTicketDetails(ticket)}
                        className="flex items-center gap-1 px-2 py-1 text-[#10a5e9] hover:bg-[#10a5e9] hover:text-white rounded text-xs font-medium transition-colors"
                        title="View Details"
                      >
                        <FaEye className="text-xs" />
                        <span>View</span>
                      </button>
                      {/* Edit Button for Refer Back Tickets */}
                      {(ticket.status === 'refer_back' || ticket.completion_status === 'refer_back') && 
                        Number(ticket.user_id) === Number(userId) && (
                        <button
                          onClick={() => navigate(`/ticket-raise?id=${ticket.id}`)}
                          className="flex items-center gap-1 px-2 py-1 text-orange-600 hover:bg-orange-600 hover:text-white rounded text-xs font-medium transition-colors"
                          title="Edit Ticket"
                        >
                          <FaEdit className="text-xs" />
                          <span>Edit</span>
                        </button>
                      )}
                      {(userRole === 'Admin' || userRole === 'admin') && ticket.status === 'pending' && (
                        <button
                          onClick={() => openApproveModal(ticket)}
                          className="flex items-center gap-1 px-2 py-1 text-green-600 hover:bg-green-600 hover:text-white rounded text-xs font-medium transition-colors"
                          title="Approve/Reject"
                        >
                          <FaEdit className="text-xs" />
                          <span>Review</span>
                        </button>
                      )}
                      {(userRole === 'developer' || userRole === 'Developer') && ticket.status === 'approved' && (
                        <button
                          onClick={() => openCompletionModal(ticket)}
                          className="flex items-center gap-1 px-2 py-1 text-blue-600 hover:bg-blue-600 hover:text-white rounded text-xs font-medium transition-colors"
                          title="Update Completion Status"
                        >
                          <FaEdit className="text-xs" />
                          <span>Update</span>
                        </button>
                      )}
                      {ticket.completion_status === 'completed' && (
                        <button
                          onClick={() => openReviewModal(ticket)}
                          className="flex items-center gap-1 px-2 py-1 text-green-600 hover:bg-green-600 hover:text-white rounded text-xs font-medium transition-colors"
                          title="Review Ticket"
                        >
                          <FaCheckCircle className="text-xs" />
                          <span>Review</span>
                        </button>
                      )}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <div>
                      <span className="text-xs text-gray-500">Priority:</span>
                      <div className="mt-0.5">{getPriorityBadge(ticket.priority)}</div>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500">Category:</span>
                      <div className="mt-0.5">{getCategoryBadge(ticket.category)}</div>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500">Admin Approval:</span>
                      <div className="mt-0.5 flex items-center">
                        {getStatusIcon(ticket.status)}
                        <span className="ml-1 text-xs capitalize">{ticket.status}</span>
                      </div>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500">Completion:</span>
                      <div className="mt-0.5 flex items-center justify-between">
                        {getCompletionStatusBadge(ticket)}
                        {ticket.completion_status === 'completed' && (
                          <div className="relative group ml-2">
                            <FaInfoCircle 
                              className="text-blue-500 cursor-help hover:text-blue-700 transition-colors" 
                              title="Kindly review the completed ticket. If all requirements are met, please proceed to close the ticket. Thank you."
                            />
                            <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                              Kindly review the completed ticket. If all requirements are met, please proceed to close the ticket. Thank you.
                              <div className="absolute top-full right-4 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                    <div className="text-xs text-gray-600">
                      <span className="font-medium">By:</span> {ticket.name || 'N/A'}
                    </div>
                    <div className="text-xs text-gray-600">
                      {formatDate(ticket.created_at)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <NoRecordFound 
            message="No tickets found"
            subMessage={(userRole === 'Admin' || userRole === 'admin' || userRole === 'developer' || userRole === 'Developer')
              ? 'No tickets have been submitted yet.' 
              : 'Get started by submitting a new change request.'}
            icon={<FaList className="h-12 w-12" />}
          />
        )}

        {/* Pagination Controls */}
        {total > ROWS_PER_PAGE && (
          <div className="flex justify-end items-center gap-2 mt-3 text-xs">
            <button
              onClick={handlePrevPage}
              disabled={page === 1}
              className={`px-2 py-1 rounded border transition-colors duration-100 ${page === 1 ? 'bg-gray-100 text-gray-300 border-gray-200' : 'bg-white text-[#10a5e9] border-[#10a5e9] hover:text-white hover:bg-[#10a5e9]'}`}
            >
              Prev
            </button>
            <span className="text-gray-600">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={handleNextPage}
              disabled={page === totalPages}
              className={`px-2 py-1 rounded border transition-colors duration-100 ${page === totalPages ? 'bg-gray-100 text-gray-300 border-gray-200' : 'bg-white text-[#10a5e9] border-[#10a5e9] hover:text-white hover:bg-[#10a5e9]'}`}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Admin Approval Modal */}
      {showApproveModal && selectedTicket && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900">
                Review Ticket: {selectedTicket.ticket_number}
              </h3>
              <button
                onClick={() => setShowApproveModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FaTimesCircle className="h-6 w-6" />
              </button>
            </div>
            <div className="p-4 sm:p-6 space-y-4">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-600 mb-2">
                  Admin Notes (Optional)
                </label>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  rows="4"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:ring-[#10a5e9] focus:border-[#10a5e9]"
                  placeholder="Add any notes or comments about this ticket..."
                ></textarea>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-end p-4 border-t gap-2">
              <button
                onClick={() => {
                  if (!isSubmitting) {
                    setShowApproveModal(false);
                    setAdminNotes('');
                  }
                }}
                disabled={isSubmitting}
                className={`flex items-center justify-center w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-md text-sm font-medium ${
                  isSubmitting 
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={() => handleApprove('approved')}
                disabled={isSubmitting}
                className={`flex items-center justify-center w-full sm:w-auto px-4 py-2 rounded-md text-sm font-medium ${
                  isSubmitting
                    ? 'bg-gray-400 text-white cursor-not-allowed'
                    : 'bg-green-600 text-white hover:bg-green-700'
                }`}
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <FaCheck className="mr-2" /> Approve
                  </>
                )}
              </button>
              <button
                onClick={() => handleApprove('refer_back')}
                disabled={isSubmitting}
                className={`flex items-center justify-center w-full sm:w-auto px-4 py-2 rounded-md text-sm font-medium ${
                  isSubmitting
                    ? 'bg-gray-400 text-white cursor-not-allowed'
                    : 'bg-yellow-600 text-white hover:bg-yellow-700'
                }`}
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <FaClock className="mr-2" /> Refer Back
                  </>
                )}
              </button>
              <button
                onClick={() => handleApprove('rejected')}
                disabled={isSubmitting}
                className={`flex items-center justify-center w-full sm:w-auto px-4 py-2 rounded-md text-sm font-medium ${
                  isSubmitting
                    ? 'bg-gray-400 text-white cursor-not-allowed'
                    : 'bg-red-600 text-white hover:bg-red-700'
                }`}
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <FaTimes className="mr-2" /> Reject
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Developer Completion Status Modal */}
      {showCompletionModal && selectedTicket && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900">
                Update Completion Status: {selectedTicket.ticket_number}
              </h3>
              <button
                onClick={() => setShowCompletionModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FaTimesCircle className="h-6 w-6" />
              </button>
            </div>
            <div className="p-4 sm:p-6 space-y-4">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-600 mb-2">
                  Completion Status <span className="text-red-500">*</span>
                </label>
                <select
                  value={completionStatus}
                  onChange={(e) => setCompletionStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:ring-[#10a5e9] focus:border-[#10a5e9]"
                  required
                >
                  <option value="">Select Status</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-600 mb-2">
                  Developer Notes (Optional)
                </label>
                <textarea
                  value={developerNotes}
                  onChange={(e) => setDeveloperNotes(e.target.value)}
                  rows="4"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:ring-[#10a5e9] focus:border-[#10a5e9]"
                  placeholder="Add notes about the work done, changes made, or additional information..."
                ></textarea>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-end p-4 border-t gap-2">
              <button
                onClick={() => setShowCompletionModal(false)}
                className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateCompletion}
                className="w-full sm:w-auto px-4 py-2 bg-[#10a5e9] text-white rounded-md hover:bg-blue-700 text-sm font-medium"
              >
                Update Status
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Review Ticket Modal */}
      {showReviewModal && selectedTicket && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900">
                Review Ticket: {selectedTicket.ticket_number}
              </h3>
              <button
                onClick={() => setShowReviewModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FaTimesCircle className="h-6 w-6" />
              </button>
            </div>
            <div className="p-4 sm:p-6 space-y-4">
              <div>
                <p className="text-sm text-gray-700 mb-4">
                  The developer has marked this ticket as completed. Please review and take action:
                </p>
                <label className="block text-xs sm:text-sm font-medium text-gray-600 mb-2">
                  Review Comment {selectedTicket.completion_status === 'completed' && <span className="text-red-500">*</span>}
                </label>
                <textarea
                  value={reviewComment}
                  onChange={(e) => setReviewComment(e.target.value)}
                  rows="4"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:ring-[#10a5e9] focus:border-[#10a5e9]"
                  placeholder="Add your review comment (required if returning to developer)..."
                ></textarea>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-between p-4 border-t gap-2">
              <button
                onClick={() => {
                  if (!isReviewSubmitting) {
                    setShowReviewModal(false);
                    setReviewComment('');
                  }
                }}
                disabled={isReviewSubmitting}
                className={`w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-md text-sm font-medium ${
                  isReviewSubmitting 
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                Cancel
              </button>
              <div className="flex gap-2 w-full sm:w-auto">
                <button
                  onClick={() => handleReviewTicket('return')}
                  disabled={isReviewSubmitting}
                  className={`flex items-center justify-center flex-1 sm:flex-none px-4 py-2 rounded-md text-sm font-medium ${
                    isReviewSubmitting
                      ? 'bg-gray-400 text-white cursor-not-allowed'
                      : 'bg-yellow-600 text-white hover:bg-yellow-700'
                  }`}
                >
                  {isReviewSubmitting ? (
                    <>
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                      Processing...
                    </>
                  ) : (
                    <>
                      <FaTimes className="mr-2" /> Return to Developer
                    </>
                  )}
                </button>
                <button
                  onClick={() => handleReviewTicket('close')}
                  disabled={isReviewSubmitting}
                  className={`flex items-center justify-center flex-1 sm:flex-none px-4 py-2 rounded-md text-sm font-medium ${
                    isReviewSubmitting
                      ? 'bg-gray-400 text-white cursor-not-allowed'
                      : 'bg-green-600 text-white hover:bg-green-700'
                  }`}
                >
                  {isReviewSubmitting ? (
                    <>
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                      Processing...
                    </>
                  ) : (
                    <>
                      <FaCheck className="mr-2" /> Close Ticket
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Ticket Details Modal */}
      {showDetailsModal && selectedTicket && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col mx-4">
            <div className="flex items-center justify-between p-4 border-b bg-white">
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900">
                Ticket Details: {selectedTicket.ticket_number}
              </h3>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FaTimesCircle className="h-6 w-6" />
              </button>
            </div>
            <div className="p-4 sm:p-6 space-y-4 overflow-y-auto flex-1">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-600">Title</label>
                  <p className="mt-1 text-xs sm:text-sm text-gray-900">{selectedTicket.title}</p>
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-600">Priority</label>
                  <p className="mt-1 text-xs sm:text-sm">{getPriorityBadge(selectedTicket.priority)}</p>
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-600">Category</label>
                  <p className="mt-1 text-xs sm:text-sm">{getCategoryBadge(selectedTicket.category)}</p>
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-600">Admin Approval</label>
                  <p className="mt-1 text-xs sm:text-sm flex items-center">
                    {getStatusIcon(selectedTicket.status)}
                    <span className="ml-2 capitalize">{selectedTicket.status}</span>
                  </p>
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-600">Completion Status</label>
                  <p className="mt-1 text-xs sm:text-sm">{getCompletionStatusBadge(selectedTicket)}</p>
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-600">Applied By</label>
                  <p className="mt-1 text-xs sm:text-sm text-gray-900">{selectedTicket.name || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-600">Date Created</label>
                  <p className="mt-1 text-xs sm:text-sm text-gray-900">
                    {formatDate(selectedTicket.created_at)}
                  </p>
                </div>
                {selectedTicket.reviewed_at && (
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-600">Reviewed At</label>
                    <p className="mt-1 text-xs sm:text-sm text-gray-900">
                      {formatDate(selectedTicket.reviewed_at)}
                    </p>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-600">Description</label>
                <div className="mt-1 text-xs sm:text-sm text-gray-900 bg-gray-50 p-3 rounded-md whitespace-pre-wrap">
                  {selectedTicket.description}
                </div>
              </div>
              {selectedTicket.admin_notes && (
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-600">Admin Notes</label>
                  <div className="mt-1 text-xs sm:text-sm text-gray-900 bg-yellow-50 p-3 rounded-md whitespace-pre-wrap">
                    {selectedTicket.admin_notes}
                  </div>
                </div>
              )}
              
              {selectedTicket.developer_notes && (
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-600">Developer Notes</label>
                  <div className="mt-1 text-xs sm:text-sm text-gray-900 bg-blue-50 p-3 rounded-md whitespace-pre-wrap">
                    {selectedTicket.developer_notes}
                  </div>
                </div>
              )}

              {/* Attached Documents Section */}
              {selectedTicket.documents && (() => {
                try {
                  const documents = typeof selectedTicket.documents === 'string' 
                    ? JSON.parse(selectedTicket.documents) 
                    : selectedTicket.documents;
                  const docArray = Array.isArray(documents) ? documents : [];
                  
                  if (docArray.length > 0) {
                    return (
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-600 mb-2">
                          Attached Documents ({docArray.length})
                        </label>
                        <div className="space-y-2">
                          {docArray.map((doc, index) => {
                            const fileName = doc.split('/').pop() || doc;
                            // Construct URL: if doc is relative path like "tickets/file.pdf", use /uploads/tickets/file.pdf
                            const fileUrl = doc.startsWith('http') 
                              ? doc 
                              : `${API_BASE_URL}/uploads/${doc}`;
                            return (
                              <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded-md">
                                <a
                                  href={fileUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-[#10a5e9] hover:underline flex items-center gap-2 text-xs sm:text-sm flex-1 truncate"
                                >
                                  <FaDownload className="text-xs" />
                                  <span className="truncate">{fileName}</span>
                                </a>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  }
                } catch (e) {
                  console.error('Error parsing documents:', e);
                }
                return null;
              })()}
              
              {/* Ticket Logs Section */}
              <div className="mt-4">
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-xs sm:text-sm font-medium text-gray-600">Ticket Activity Log</label>
                  {returnCount > 0 && (
                    <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-semibold">
                      Returned {returnCount} time{returnCount > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                <div className="bg-gray-50 p-3 rounded-md max-h-60 overflow-y-auto">
                  {ticketLogs.length > 0 ? (
                    <div className="space-y-3">
                      {ticketLogs.map((log, index) => (
                        <div key={log.id || index} className="border-l-4 border-[#10a5e9] pl-3 pb-2">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs font-semibold text-gray-900 capitalize">
                                  {log.action?.replace(/_/g, ' ')}
                                </span>
                                {log.user_name && (
                                  <span className="text-xs text-gray-500">by {log.user_name}</span>
                                )}
                                {log.user_role && (
                                  <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-800 rounded">
                                    {log.user_role}
                                  </span>
                                )}
                              </div>
                              {log.comment && (
                                <p className="text-xs text-gray-700 mt-1 italic">"{log.comment}"</p>
                              )}
                              {log.new_value && (
                                <p className="text-xs text-gray-600 mt-1">
                                  {JSON.parse(log.new_value).completion_status && (
                                    <span className="capitalize">
                                      Status: {JSON.parse(log.new_value).completion_status.replace(/_/g, ' ')}
                                    </span>
                                  )}
                                  {JSON.parse(log.new_value).return_count && (
                                    <span className="ml-2 text-red-600">
                                      (Return #{JSON.parse(log.new_value).return_count})
                                    </span>
                                  )}
                                </p>
                              )}
                              <p className="text-xs text-gray-400 mt-1">
                                {new Date(log.created_at).toLocaleString()}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500 text-center py-2">No activity logs available</p>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end p-4 border-t bg-white">
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  setTicketLogs([]);
                  setReturnCount(0);
                }}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 text-sm font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketList;
